'use client';

import { Button } from '@savvy-wealth/savvy-kit/components/button';
import Banner from '@savvy-wealth/savvy-kit/patterns/banner';

import type { SkewRefreshState } from './controller';

type BannerContent = {
  readonly variant: 'info' | 'destructive';
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string | null;
};

/**
 * Every user-visible skew state. None is dismissible: D3 accepts "the affected operation
 * keeps erroring until the user refreshes" precisely *because* the banner stays visible, so
 * a dismiss control would remove the mitigation that decision rests on.
 */
const BANNER_CONTENT: Record<Exclude<SkewRefreshState, 'idle'>, BannerContent> = {
  deferred: {
    variant: 'info',
    title: 'Update ready',
    description: "We'll refresh when this finishes.",
    actionLabel: null,
  },
  'refresh-ready': {
    variant: 'info',
    title: 'Update ready',
    description: 'Refresh to pick up the latest version of Savvy.',
    actionLabel: 'Refresh now',
  },
  'breaker-tripped': {
    variant: 'destructive',
    title: 'Savvy is out of date',
    description:
      'Refreshing did not pick up the latest version. Try again, or contact support if this keeps happening.',
    actionLabel: 'Refresh now',
  },
};

export type SkewRefreshBannerViewProps = {
  readonly state: SkewRefreshState;
  readonly onRefresh: () => void;
};

export function SkewRefreshBannerView({ state, onRefresh }: SkewRefreshBannerViewProps) {
  const content = state === 'idle' ? null : BANNER_CONTENT[state];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      // Mounted even at idle: screen readers routinely miss content that arrives in the same
      // tick as the live region containing it. Stacked above every other layer because this
      // banner is the recovery affordance — hidden behind a modal, it can't be acted on.
      // Focus is never moved programmatically; the action is reachable by Tab, and stealing
      // focus would interrupt whatever the user is mid-way through typing or reading.
      className="pointer-events-none fixed inset-x-0 top-0 z-banner flex justify-center p-3"
    >
      {content && (
        <Banner
          // Banner defaults to role="alert", which is assertive and cuts a screen reader off
          // mid-sentence; the wrapper above owns the polite live region instead.
          role="presentation"
          variant={content.variant}
          title={content.title}
          description={content.description}
          className="pointer-events-auto w-auto max-w-lg shadow-lg"
          actionButton={
            content.actionLabel === null ? null : (
              <Button size="sm" onClick={onRefresh}>
                {content.actionLabel}
              </Button>
            )
          }
        />
      )}
    </div>
  );
}
